import { useCallback, useMemo, useRef, useState } from "react"
import { useParams } from "react-router"
import { defineQuery } from "@nova/queryable"
import { useBatchPilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { Card } from "features/pilot-tracking/MetricLayout"
import VideoLayer, { VideoLayerProps } from "features/map/layers/video/Video.class"
import { DeckGLOverlay, fitMapToBounds } from "features/map/MapPreview"
import { Map, MapRef } from "react-map-gl/dist/maplibre"
import { useMapStylesJSON } from "features/map/control/selectors"
import { StyleSpecification } from "maplibre-gl"
import { VideoMetadata } from "@nova/slice-video"
import { droneIconColors } from "features/map/layers/video/VideoDroneLayer"
import { ButtonSecondary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { isNullIsland } from "@nova/calculate-geospatial"
import { useTelemetryPlayback } from "tools/hooks/useTelemetryPlayback"
import { PlaybackControls } from "components/PlaybackControls"

const logsQuery = defineQuery((missionId: string) => ({
    resource: "logs",
    select: ["latitude", "longitude", "altitude", "timestamp", "heading"],
    filters: [{ field: "mission_id", op: "=", value: missionId }],
    orderBy: [{ field: "id", dir: "ASC" }],
}))

const takeoffQuery = defineQuery((missionId: string) => ({
    resource: "metadata",
    select: ["takeoff_latitude", "takeoff_longitude"],
    filters: [{ field: "mission_id", op: "=", value: missionId }],
}))

const MAX_PLAUSIBLE_GAP_MS = 10 * 60 * 60 * 1000

function isValidLog(d: {
    longitude: number | null
    latitude: number | null
    timestamp: Date | null
}) {
    return (
        d.longitude !== null &&
        d.latitude !== null &&
        !isNaN(d.longitude) &&
        !isNaN(d.latitude) &&
        !isNullIsland({ lat: d.latitude, lng: d.longitude }) &&
        d.longitude >= -180 &&
        d.longitude <= 180 &&
        d.latitude >= -90 &&
        d.latitude <= 90
    )
}

function buildLayers(
    flightData: { coords: VideoMetadata; timestamps: number[] },
    playbackIndex: number,
    missionId: string
) {
    const lo = Math.floor(playbackIndex)
    const hi = Math.min(lo + 1, flightData.coords.length - 1)
    const t = playbackIndex - lo
    const lerp = (a: number, b: number) => a + t * (b - a)

    const interpolatedCoord = [
        lerp(flightData.coords[lo][0], flightData.coords[hi][0]),
        lerp(flightData.coords[lo][1], flightData.coords[hi][1]),
        lerp(flightData.coords[lo][2], flightData.coords[hi][2]),
        flightData.coords[lo][3],
        lerp(flightData.coords[lo][4], flightData.coords[hi][4]),
        flightData.coords[lo][5],
        0,
        0,
    ] satisfies VideoMetadata[number]

    const shared = {
        flightId: missionId,
        frameNumber: 1,
        mediaType: "image",
        frameEnabled: false,
        crosshairEnabled: false,
        live: true,
        color: droneIconColors[0],
        getVideoElement: () => null,
    } satisfies Partial<VideoLayerProps>

    return [
        new VideoLayer({
            ...shared,
            id: "mission-video-path",
            data: flightData.coords as VideoMetadata,
            flightPathEnabled: true,
            droneEnabled: false,
        }),
        new VideoLayer({
            ...shared,
            id: "mission-video-drone",
            data: [interpolatedCoord] as VideoMetadata,
            flightPathEnabled: false,
            droneEnabled: true,
        }),
    ]
}

const FlightPathCard = () => {
    const { missionId = "" } = useParams()
    const queries = useMemo(
        () => ({
            logs: logsQuery(missionId),
            takeoff: takeoffQuery(missionId),
        }),
        [missionId]
    )
    const { data, isLoading } = useBatchPilotTrackingQuery(queries)
    const logs = data?.logs
    const takeoff = data?.takeoff

    const mapStylesJSON = useMapStylesJSON()
    const flatMapStyle = useMemo(() => {
        const { terrain: _terrain, ...rest } = mapStylesJSON as StyleSpecification & {
            terrain?: unknown
        }
        return rest as StyleSpecification
    }, [mapStylesJSON])

    const mapRef = useRef<MapRef | null>(null)
    const [isZoomed, setIsZoomed] = useState(false)

    const flightData = useMemo(() => {
        const valid = logs?.filter(isValidLog) ?? []
        if (!valid.length) {
            const t = takeoff?.[0]
            if (
                t?.takeoff_latitude == null ||
                t.takeoff_longitude == null ||
                isNullIsland({ lat: t.takeoff_latitude, lng: t.takeoff_longitude })
            )
                return null
            const coord: VideoMetadata[number] = [
                t.takeoff_longitude,
                t.takeoff_latitude,
                0,
                NaN,
                NaN,
                NaN,
                0,
                0,
            ]
            return { coords: [coord], timestamps: [0] }
        }
        const rawTimes = valid.map((d) => {
            if (!d.timestamp) return null
            const ms = new Date(d.timestamp).getTime()
            return Number.isFinite(ms) ? ms : null
        })
        const firstValid = rawTimes.find((t) => t !== null) ?? 0
        let last = firstValid
        const timestamps = rawTimes.map((t) => {
            if (t !== null && Math.abs(t - last) <= MAX_PLAUSIBLE_GAP_MS) last = t
            return last
        })
        const coords = valid.map((d): VideoMetadata[number] => [
            d.longitude!,
            d.latitude!,
            0,
            d.heading == null ? NaN : 0,
            d.heading ?? NaN,
            d.heading == null ? NaN : 0,
            0,
            0,
        ])
        return { coords, timestamps }
    }, [logs, takeoff])

    const disablePlayback = useMemo(() => {
        const valid = logs?.filter(isValidLog) ?? []
        if (!valid.length) return true
        const missing = valid.filter((d) => !d.timestamp).length
        return missing / valid.length > 0.8
    }, [logs])

    const { playbackIndex, isPlaying, handlePlayPause, handleSeek } = useTelemetryPlayback({
        timestamps: flightData?.timestamps ?? [],
    })

    const layers = useMemo(
        () => (flightData ? buildLayers(flightData, playbackIndex, missionId) : []),
        [flightData, playbackIndex, missionId]
    )

    const zoomToFlight = useCallback(() => {
        if (!mapRef.current || !flightData) return
        fitMapToBounds(
            mapRef.current,
            flightData.coords.map((c) => [c[0], c[1]])
        )
        setIsZoomed(true)
    }, [flightData])

    return (
        <Card className="grow flex flex-col !p-0 min-h-64">
            <div className="flex-1">
                {isLoading ? (
                    <div className="w-full h-full animate-pulse bg-gray-700" />
                ) : !flightData ? (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                        No flight data available
                    </div>
                ) : (
                    <div className="relative w-full h-full">
                        <Map
                            ref={mapRef}
                            mapStyle={flatMapStyle}
                            initialViewState={{ latitude: 0, longitude: 0, zoom: 3 }}
                            minZoom={3}
                            attributionControl={false}
                            onLoad={zoomToFlight}
                            onMove={() => setIsZoomed(false)}
                            style={{ width: "100%", height: "100%" }}
                        >
                            <DeckGLOverlay layers={layers} />
                        </Map>
                        {!isZoomed && (
                            <div className="absolute bottom-4 right-4 z-20">
                                <ButtonSecondary
                                    title="Zoom To Flight"
                                    LeftIcon={Icons.ZoomTo}
                                    onClick={zoomToFlight}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
            {flightData && (
                <div className="px-4 py-3">
                    <PlaybackControls
                        playbackIndex={playbackIndex}
                        isPlaying={isPlaying}
                        timestamps={flightData.timestamps}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        disablePlayback={disablePlayback}
                    />
                </div>
            )}
        </Card>
    )
}

export { FlightPathCard }
